import React, { useState, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Divider,
  Link,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/system";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { ReactComponent as QMentisImageTagline } from "../../images/QMentisAITagline.svg";
import QMentis from "../../images/QMentis.svg";
import { useNavigate } from "react-router-dom";
import { forgotPassword } from "../../services/webServices";
import HCaptcha from "@hcaptcha/react-hcaptcha";//for using characters captcha
import RefreshIcon from "@mui/icons-material/Refresh";
 
const StyledBox = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  height: "100vh",
  width: "100%",
  overflow: "hidden",
  backgroundColor: "#FFFFFF",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
}));
 
const Split = styled(Box)(({ theme, side }) => ({
  width: side === "left" ? "65%" : "25%",
  height: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  background:
    side === "left"
      ? `linear-gradient(-135deg, rgba(0, 116, 255, 0.2), rgba(226, 78, 94, 0.2))`
      : "#F6F3F2",
  [theme.breakpoints.down("md")]: {
    width: "100%",
    height: side === "left" ? "30%" : "70%",
  },
}));
 
const StyledButton = styled(Button)(({ theme }) => ({
  marginTop: "16px",
  width: "100%",
  textTransform: "none",
  borderRadius: "4px",
  backgroundColor: "#0094FB",
  color: "#FFFFFF",
  "&:hover": {
    backgroundColor: "#0077CC",
  },
  "&.Mui-disabled": {
    backgroundColor: "#CCCCCC",
    color: "#888888",
  },
}));
 
const HCAPTCHA_SITE_KEY = "YOUR_HCAPTCHA_SITE_KEY";
 
const CustomTextCaptcha = ({ onVerify }) => {
  const [captchaText, setCaptchaText] = useState("");
  const [userInput, setUserInput] = useState("");
  const [captchaError, setCaptchaError] = useState("");
  const [verified, setVerified] = useState(false);
 
  const generateCaptcha = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
    let result = "";
    for (let i = 0; i < 6; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setCaptchaText(result);
    setUserInput("");
    setCaptchaError("");
    setVerified(false);
    onVerify(false, null);
  };
 
  React.useEffect(() => {
    generateCaptcha();
  }, []);
 
  const handleInputChange = (e) => {
    setUserInput(e.target.value);
    setCaptchaError("");
    setVerified(false);
  };
 
  const verifyCaptcha = () => {
    if (userInput === captchaText) {
      onVerify(true, "custom-captcha-token");
      setCaptchaError("");
      setVerified(true);
    } else {
      setCaptchaError("Invalid CAPTCHA. Please try again.");
      onVerify(false, null);
      setVerified(false);
    }
  };
 
  return (
    <Box sx={{ width: "100%", mb: 2 }}>
      <Typography variant="body2" sx={{ mb: 1 }}>
        Please enter the characters below:
      </Typography>
      <Box
        sx={{
          p: 2,
          mb: 2,
          backgroundColor: "#f5f5f5",
          display: "flex",
          justifyContent: "center",
          fontFamily: "monospace",
          fontSize: "18px",
          letterSpacing: "5px",
          borderRadius: "4px",
          position: "relative",
          userSelect: "none",
          fontWeight: "bold",
          background: "linear-gradient(45deg, #f5f5f5, #e8e8e8)",
        }}
      >
        {captchaText.split("").map((char, i) => (
          <span
            key={i}
            style={{
              display: "inline-block",
              transform: `rotate(${Math.random() * 10 - 5}deg) translateY(${
                Math.random() * 6 - 3
              }px)`,
              color: `hsl(${Math.random() * 360}, 70%, 40%)`,
            }}
          >
            {char}
          </span>
        ))}
        <Button
          size="small"
          sx={{
            position: "absolute",
            right: 0,
            top: 0,
            fontSize: "12px",
            color: "#0094FB",
            minWidth: 0,
            padding: "4px",
          }}
          onClick={generateCaptcha}
        >
          <RefreshIcon fontSize="small" />
        </Button>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Enter characters shown above"
            value={userInput}
            onChange={handleInputChange}
            error={!!captchaError}
            fullWidth
            sx={{
              flexGrow: 1,
              "& .MuiOutlinedInput-root": {
                borderColor: verified ? "#4caf50" : undefined,
              },
            }}
            InputProps={{
              endAdornment: verified && (
                <Box
                  sx={{
                    color: "#4caf50",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <span role="img" aria-label="verified">
                    ✓
                  </span>
                </Box>
              ),
            }}
          />
          <Button
            variant="outlined"
            onClick={verifyCaptcha}
            disabled={!userInput || verified}
            sx={{
              height: "40px",
              color: verified ? "#4caf50" : "#0094FB",
              borderColor: verified ? "#4caf50" : undefined,
              "&.Mui-disabled": {
                color: verified ? "#4caf50" : undefined,
                borderColor: verified ? "#4caf50" : undefined,
              },
            }}
          >
            {verified ? "Verified" : "Verify"}
          </Button>
        </Box>
        {captchaError && (
          <Typography variant="caption" color="error" sx={{ mt: 1 }}>
            {captchaError}
          </Typography>
        )}
      </Box>
    </Box>
  );
};
 
function ForgotPassword() {
  const [formValues, setFormValues] = useState({ email: "" });
  const [formErrors, setFormErrors] = useState({});
  const [error, setError] = useState("");
  const [severity, setSeverity] = useState("error");
  const [captchaToken, setCaptchaToken] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [useCustomCaptcha, setUseCustomCaptcha] = useState(true); // Set to false if using hCaptcha
  const captchaRef = useRef(null);
 
  const navigate = useNavigate();
 
  const handleInputChange = (event) => {
    const { id, value } = event.target;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (id === "email") {
      if (!emailRegex.test(value) && value.trim() !== "") {
        setError("Invalid email format.");
      } else {
        setError("");
      }
    }
 
    setFormValues({ ...formValues, [id]: value });
    setFormErrors({ ...formErrors, [id]: value.trim() ? "" : formErrors[id] });
  };
 
  const handleCaptchaChange = (token) => {
    setCaptchaToken(token);
  };
 
  const handleCustomCaptchaVerify = (isVerified, token) => {
    if (isVerified) {
      setCaptchaToken(token);
    } else {
      setCaptchaToken("");
    }
  };
 
  const handlePasswordReset = async (event) => {
    event.preventDefault();
    const email = document.getElementById("email").value;
 
    const errors = {};
    if (!email.trim()) {
      errors["email"] = "Please enter your email address.";
    }
 
    setFormErrors(errors);
 
    if (Object.keys(errors).length > 0) return;
 
    if (!captchaToken) {
      setError("Please complete the CAPTCHA verification.");
      return;
    }
 
    setIsSubmitting(true);
 
    try {
      const response = await forgotPassword({
        username: email,
        captchaToken: captchaToken,
      });
      setSeverity("success");
      setError("A password reset link has been sent to your email.");
    } catch (error) {
      setSeverity("error");
      if (error.response?.data?.message?.includes("CAPTCHA")) {
        setError("CAPTCHA verification failed. Please try again.");
        setCaptchaToken("");
      } else {
        setError(
          error.response?.data?.message ||
            "An error occurred. Please try again."
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };
 
  const isSubmitDisabled = isSubmitting || !captchaToken || !formValues.email;
 
  return (
    <StyledBox sx={{ display: "flex", width: "100%" }}>
      <Split side="left" sx={{ width: "65%" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            textAlign: "left",
            px: { xs: 2, sm: 4 },
            maxWidth: "500px",
            width: "100%",
            top: "-20px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              mb: 2,
            }}
          >
            <img
              src={QMentis}
              alt="QMentis Logo"
              style={{
                width: "55%",
                height: "55%",
                objectFit: "contain",
                marginBottom: "-20px",
                marginLeft: "-28px",
              }}
            />
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{
                fontSize: { xs: "15px", sm: "20px" },
                fontWeight: "350",
                mt: 1,
              }}
            >
              Revolutionising AI based QE
            </Typography>
          </Box>
 
          {/* Divider */}
          <Divider
            sx={{
              borderColor: "grey.400",
              borderWidth: 1,
              width: "100%",
              mb: 1,
            }}
          />
          <Typography
            variant="h4"
            color="textPrimary"
            sx={{
              fontSize: { xs: "20px", sm: "35px" },
              fontWeight: "400",
              lineHeight: 1.5,
              mt: 2,
              textAlign: "left",
            }}
          >
            Power your QE journey forward
            <br />
            through{" "}
            <span style={{ color: "#0094FB", fontWeight: "bold" }}>
              QMentisAI™
            </span>{" "}
            - your
            <br />
            gateway into smarter testing
          </Typography>
        </Box>
      </Split>
 
      <Split side="right" sx={{ width: "35%" }}>
        <Box
          sx={{
            backgroundColor: "white",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            padding: 3,
            height: "100vh",
            width: "100%",
            overflowY: "auto",
          }}
        >
          <Box
            sx={{
              width: "80%",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
            }}
          >
            <QMentisImageTagline />
          </Box>
 
          <Divider
            sx={{
              my: 2,
              borderColor: "grey.400",
              borderWidth: 1,
              width: "80%",
            }}
          />
 
          <Box
            sx={{
              width: "80%",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              mb: 2,
              mt: 4,
            }}
          >
            <Typography fontSize="15px" textAlign="left" color="black">
              Please enter the email address that you used to register, and we
              will send you a link to reset your password via mail.
            </Typography>
          </Box>
 
          <form onSubmit={handlePasswordReset} style={{ width: "80%" }}>
            <TextField
              label="Email ID"
              id="email"
              variant="outlined"
              fullWidth
              margin="normal"
              size="small"
              value={formValues.email}
              error={!!formErrors.email}
              helperText={formErrors.email}
              onChange={handleInputChange}
              sx={{ marginBottom: "16px" }}
            />
 
            {useCustomCaptcha ? (
              <CustomTextCaptcha onVerify={handleCustomCaptchaVerify} />
            ) : (
              <Box sx={{ my: 2, display: "flex", justifyContent: "center" }}>
                <HCaptcha
                  ref={captchaRef}
                  sitekey={HCAPTCHA_SITE_KEY}
                  onVerify={handleCaptchaChange}
                />
              </Box>
            )}
 
            <Typography color={severity === "error" ? "error" : "primary"}>
              {error}
            </Typography>
 
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <StyledButton
                type="submit"
                variant="contained"
                startIcon={!isSubmitting && <ArrowForwardIcon />}
                disabled={isSubmitDisabled}
                sx={{
                  opacity: isSubmitDisabled ? 0.7 : 1,
                  cursor: isSubmitDisabled ? "not-allowed" : "pointer",
                }}
              >
                {isSubmitting ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Submit"
                )}
              </StyledButton>
            </Box>
 
            {!captchaToken && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", textAlign: "center", mt: 1 }}
              >
                Please verify the CAPTCHA to enable the submit button
              </Typography>
            )}
          </form>
 
          <Box
            sx={{
              width: "80%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              mb: 2,
              mt: 4,
            }}
          >
            <Typography>
              Return to&nbsp;
              <Link
                variant="body2"
                sx={{ mt: 1, fontSize: "14px", cursor: "pointer" }}
                onClick={() => navigate("/")}
              >
                Login
              </Link>
            </Typography>
          </Box>
        </Box>
      </Split>
    </StyledBox>
  );
}
 
export default ForgotPassword;
 
 
 